import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CpmBadge, CpmButton, CpmChip } from '../../../components/design-system';
import { listCatalog, listStates } from '../../../services/reportsAdminService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    CatalogListRow,
    CatalogSummary,
    PlaceLoadState,
} from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { searchQueryFrom, shortReportId } from '../helpers';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import CopyIdButton from '../components/CopyIdButton';
import FilteredEmpty from '../components/FilteredEmpty';
import InfiniteScrollSentinel from '../components/InfiniteScrollSentinel';
import InfoI from '../components/InfoI';
import KindBadge from '../components/KindBadge';
import PlaceStateFrames from '../components/PlaceStateFrames';
import { ReportsAlert, ReportsWait } from '../components/ReportsDialog';
import StickySortHead from '../components/StickySortHead';
import CatalogFilters, { EMPTY_CATALOG_FILTERS, type CatalogFilterState } from './CatalogFilters';
import PencilButton from '../components/PencilButton';
import ReportFormModal from './ReportFormModal';

const COLS =
    'minmax(0, 2.1fr) minmax(0, 1.05fr) minmax(0, 0.95fr) minmax(0, 1.45fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 0.85fr) 56px';

function FileDot({ bound, kind }: { bound: boolean; kind: CatalogListRow['kind'] }) {
    const color = bound
        ? 'var(--cpm-success-dot)'
        : kind === 'GENERATED'
          ? 'var(--cpm-shortfall-dot)'
          : 'var(--cpm-warning-dot)';
    const tip = bound ? REPORTS_TIPS.CAN_BUILD : REPORTS_TIPS.CANNOT_BUILD;
    return (
        <span className="inline-flex items-center" style={{ gap: 6 }}>
            <span
                style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: color,
                }}
            />
            <span style={{ fontSize: 12, fontWeight: 700 }}>
                {bound ? REPORTS_COPY.CAN_BUILD : REPORTS_COPY.CANNOT_BUILD}
            </span>
            <InfoI title={tip.title} body={tip.body} />
        </span>
    );
}

export default function CatalogBody() {
    const [filters, setFilters] = useState<CatalogFilterState>(EMPTY_CATALOG_FILTERS);
    const debouncedSearch = useDebouncedValue(filters.search, 300);
    const searchQuery = searchQueryFrom(debouncedSearch);
    const [sort, setSort] = useState<{ key: 'name' | 'brandsOn' | 'kind'; dir: 'asc' | 'desc' }>({
        key: 'name',
        dir: 'asc',
    });
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState<CatalogListRow[]>([]);
    const [total, setTotal] = useState(0);
    const [place, setPlace] = useState<PlaceLoadState>('idle');
    const [states, setStates] = useState<CpmState[]>([]);
    const [statesLoading, setStatesLoading] = useState(true);
    const [statesError, setStatesError] = useState(false);
    const [modal, setModal] = useState<{ mode: 'create' | 'edit'; reportID?: string } | null>(null);
    const [savedNote, setSavedNote] = useState<{ mode: 'create' | 'edit'; name: string } | null>(
        null
    );
    const [listBusy, setListBusy] = useState(false);
    const [moreBusy, setMoreBusy] = useState(false);
    const [moreFailed, setMoreFailed] = useState(false);
    const [summary, setSummary] = useState<CatalogSummary | null>(null);
    const readyOnce = useRef(false);

    const hasFilters =
        Boolean(searchQuery) ||
        filters.states.length > 0 ||
        filters.categories.length > 0 ||
        filters.kinds.length > 0 ||
        filters.activeStatus.length > 0 ||
        filters.enabledNowhere ||
        filters.noGenerator;

    const loadStates = useCallback(async () => {
        setStatesLoading(true);
        setStatesError(false);
        try {
            setStates(await listStates());
        } catch {
            setStatesError(true);
        } finally {
            setStatesLoading(false);
        }
    }, []);

    const load = useCallback(
        async (nextPage: number, append: boolean) => {
            if (!append) {
                setMoreFailed(false);
                if (readyOnce.current) setListBusy(true);
                else setPlace('loading');
            } else {
                setMoreBusy(true);
                setMoreFailed(false);
            }
            try {
                const data = await listCatalog({
                    search: searchQuery || undefined,
                    states: filters.states.length ? filters.states : undefined,
                    categories: filters.categories.length ? filters.categories : undefined,
                    kinds: filters.kinds.length ? filters.kinds : undefined,
                    activeStatus:
                        filters.activeStatus.length === 1 ? filters.activeStatus[0] : undefined,
                    enabledNowhere: filters.enabledNowhere || undefined,
                    noGenerator: filters.noGenerator || undefined,
                    sort: sort.key,
                    sortDir: sort.dir,
                    page: nextPage,
                    limit: 50,
                });
                setRows((prev) => (append ? [...prev, ...data.reports] : data.reports));
                setTotal(data.total);
                setPage(nextPage);
                if (data.summary) setSummary(data.summary);
                readyOnce.current = true;
                setPlace('ready');
            } catch {
                if (append) setMoreFailed(true);
                else if (!readyOnce.current) setPlace('error');
            } finally {
                setListBusy(false);
                setMoreBusy(false);
            }
        },
        [
            searchQuery,
            filters.states,
            filters.categories,
            filters.kinds,
            filters.activeStatus,
            filters.enabledNowhere,
            filters.noGenerator,
            sort,
        ]
    );

    useEffect(() => {
        void loadStates();
    }, [loadStates]);

    useEffect(() => {
        void load(1, false);
    }, [load]);

    const catalogTotal = summary ? summary.canBuild + summary.cannotBuild : total;
    const showingText = hasFilters
        ? `Showing ${total} of ${catalogTotal} reports`
        : `${catalogTotal} reports in the catalogue`;
    const filterTokenCount =
        filters.states.length +
        filters.categories.length +
        filters.kinds.length +
        filters.activeStatus.length +
        (filters.enabledNowhere ? 1 : 0) +
        (filters.noGenerator ? 1 : 0);
    const filteredEmptyDetail = [
        searchQuery ? `The search is “${searchQuery}”.` : '',
        filterTokenCount
            ? `${filterTokenCount} ${filterTokenCount === 1 ? 'thing is on.' : 'things are on.'}`
            : '',
    ]
        .filter(Boolean)
        .join(' ');

    const stats = useMemo(() => {
        if (summary) return summary;
        return {
            canBuild: rows.filter((row) => row.generatorBound).length,
            cannotBuild: rows.filter((row) => !row.generatorBound).length,
            alwaysOn: rows.filter((row) => row.kind === 'ALWAYS_ON').length,
            dead: rows.filter((row) => row.kind === 'DEAD').length,
            onForZero: rows.filter((row) => row.kind !== 'ALWAYS_ON' && row.brandsOn === 0).length,
        };
    }, [rows, summary]);

    const toggleSort = (key: 'name' | 'brandsOn' | 'kind') => {
        setSort((prev) =>
            prev.key === key
                ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key, dir: 'asc' }
        );
    };

    const trueEmpty = place === 'ready' && total === 0 && !hasFilters;
    const filteredEmpty = place === 'ready' && total === 0 && hasFilters;

    return (
        <div>
            {(place === 'ready' && !trueEmpty) || listBusy ? (
                <div style={{ padding: '16px 20px 22px' }}>
                    <CatalogFilters
                        value={filters}
                        onChange={(next) => setFilters(next)}
                        states={states}
                        statesLoading={statesLoading}
                        statesError={statesError}
                        onRetryStates={() => void loadStates()}
                        disabled={listBusy}
                        onNewReport={() => setModal({ mode: 'create' })}
                    />
                    <div
                        className="flex items-baseline justify-between flex-wrap"
                        style={{ gap: 16, margin: '16px 0 10px' }}
                    >
                        <div
                            style={{
                                fontSize: 12.5,
                                fontWeight: 800,
                                color: 'var(--cpm-text-primary)',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {showingText}
                        </div>
                        <div
                            className="flex items-center flex-wrap"
                            style={{
                                gap: 14,
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: 'var(--cpm-text-secondary)',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            <MetaStat
                                label={`${stats.canBuild} can build a file`}
                                tip={REPORTS_TIPS.CAN_BUILD}
                            />
                            <MetaStat
                                label={`${stats.cannotBuild} cannot build a file`}
                                tip={REPORTS_TIPS.CANNOT_BUILD}
                            />
                            <MetaStat
                                label={`${stats.alwaysOn} always on`}
                                tip={REPORTS_TIPS.KIND_ALWAYS_ON}
                            />
                            <MetaStat
                                label={`${stats.dead} doesn’t run yet`}
                                tip={REPORTS_TIPS.KIND_DEAD}
                            />
                            <MetaStat
                                label={`${stats.onForZero} on for 0 store brands`}
                                tip={REPORTS_TIPS.ON_FOR_STORE_BRANDS}
                            />
                        </div>
                    </div>
                    {savedNote ? (
                        <div style={{ marginBottom: 12 }}>
                            <ReportsAlert
                                tone="success"
                                title={
                                    savedNote.mode === 'create'
                                        ? `Saved. “${savedNote.name}” is in the catalogue.`
                                        : `Saved. “${savedNote.name}” was updated.`
                                }
                                body={`The row above reflects it, and the count is now ${catalogTotal}. Nothing has been turned on for a store brand.`}
                            />
                        </div>
                    ) : null}
                    {listBusy ? (
                        <div>
                            <div style={{ paddingBottom: 14 }}>
                                <ReportsWait label="Matching the catalogue to that search and those filters…" />
                            </div>
                            <div
                                className="overflow-hidden"
                                style={{
                                    border: '1px solid var(--cpm-border-default)',
                                    borderRadius: 10,
                                }}
                            >
                                {Array.from({ length: 4 }, (_, i) => (
                                    <div
                                        key={i}
                                        className="cpm-sk"
                                        style={{ height: 56, borderRadius: 0 }}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {!listBusy ? (
                        <>
                            {filteredEmpty ? (
                                <FilteredEmpty
                                    title="No report in the catalogue matches what you have on"
                                    restated={filteredEmptyDetail || undefined}
                                    totalInPlace={`${catalogTotal} reports are in the catalogue with nothing on`}
                                    onClear={() => setFilters(EMPTY_CATALOG_FILTERS)}
                                />
                            ) : (
                                <div
                                    className="overflow-auto"
                                    style={{
                                        maxHeight: 580,
                                        border: '1px solid var(--cpm-border-default)',
                                        borderRadius: 10,
                                    }}
                                >
                                    <div
                                        className="sticky top-0 z-[3] grid"
                                        style={{ gridTemplateColumns: COLS, minWidth: 1060 }}
                                    >
                                        <StickySortHead
                                            label="Report"
                                            sortable
                                            active={sort.key === 'name'}
                                            dir={sort.dir}
                                            onSort={() => toggleSort('name')}
                                        />
                                        <StickySortHead label="Categories" />
                                        <StickySortHead label="States" />
                                        <StickySortHead
                                            label="Kind"
                                            sortable
                                            active={sort.key === 'kind'}
                                            dir={sort.dir}
                                            onSort={() => toggleSort('kind')}
                                        />
                                        <StickySortHead label="File" />
                                        <StickySortHead
                                            label="Store brands on"
                                            align="right"
                                            sortable
                                            active={sort.key === 'brandsOn'}
                                            dir={sort.dir}
                                            onSort={() => toggleSort('brandsOn')}
                                        />
                                        <StickySortHead label="Status" />
                                        <StickySortHead label="" align="right" />
                                    </div>
                                    {rows.map((row) => (
                                        <div
                                            key={row._id}
                                            className="grid items-center cursor-pointer"
                                            style={{
                                                gridTemplateColumns: COLS,
                                                minWidth: 1060,
                                                borderTop: '1px solid var(--cpm-border-default)',
                                            }}
                                            onClick={() =>
                                                setModal({ mode: 'edit', reportID: row.reportID })
                                            }
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background =
                                                    'var(--cpm-purple-tint-3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            <div style={{ padding: '12px 16px' }}>
                                                <div
                                                    style={{
                                                        fontSize: 13.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    {row.name}
                                                </div>
                                                <div
                                                    className="flex items-center"
                                                    style={{ gap: 8, marginTop: 4 }}
                                                >
                                                    <span
                                                        style={{
                                                            fontFamily: 'var(--cpm-font-mono)',
                                                            fontSize: 10.5,
                                                            fontWeight: 700,
                                                            color: 'var(--cpm-text-tertiary)',
                                                        }}
                                                    >
                                                        {shortReportId(row.reportID)}
                                                    </span>
                                                    <CopyIdButton id={row.reportID} />
                                                </div>
                                            </div>
                                            <div
                                                className="flex flex-wrap"
                                                style={{ gap: 4, padding: '12px 16px' }}
                                            >
                                                {row.categories.map((category) => (
                                                    <CpmChip key={category} variant="tag">
                                                        {category}
                                                    </CpmChip>
                                                ))}
                                            </div>
                                            <div style={{ padding: '12px 16px' }}>
                                                {row.allStates ? (
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: 'var(--cpm-text-secondary)',
                                                        }}
                                                    >
                                                        {REPORTS_COPY.ALL_STATES}
                                                    </span>
                                                ) : (
                                                    <div
                                                        className="flex flex-wrap"
                                                        style={{ gap: 4 }}
                                                    >
                                                        {row.states.map((state) => (
                                                            <CpmChip key={state} variant="tag">
                                                                {state}
                                                            </CpmChip>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ padding: '12px 16px' }}>
                                                <KindBadge kind={row.kind} />
                                            </div>
                                            <div style={{ padding: '12px 16px' }}>
                                                <FileDot
                                                    bound={row.generatorBound}
                                                    kind={row.kind}
                                                />
                                            </div>
                                            <div
                                                style={{ padding: '12px 16px', textAlign: 'right' }}
                                            >
                                                {row.kind === 'ALWAYS_ON' ? (
                                                    <span
                                                        className="inline-flex items-center justify-end"
                                                        style={{ gap: 6 }}
                                                    >
                                                        <span
                                                            style={{
                                                                fontSize: 11.5,
                                                                fontWeight: 800,
                                                                color: 'var(--cpm-text-secondary)',
                                                            }}
                                                        >
                                                            {REPORTS_COPY.EVERY_STORE_BRAND}
                                                        </span>
                                                        <InfoI
                                                            title={REPORTS_TIPS.NOT_A_TOGGLE.title}
                                                            body={REPORTS_TIPS.NOT_A_TOGGLE.body}
                                                        />
                                                    </span>
                                                ) : (
                                                    <div>
                                                        <div
                                                            style={{
                                                                fontSize: 13.5,
                                                                fontWeight: 800,
                                                                color: 'var(--cpm-text-primary)',
                                                                fontVariantNumeric: 'tabular-nums',
                                                            }}
                                                        >
                                                            {row.brandsOn}
                                                        </div>
                                                        {row.brandsOn === 0 ? (
                                                            <div
                                                                style={{
                                                                    marginTop: 2,
                                                                    fontSize: 9.5,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.06em',
                                                                    textTransform: 'uppercase',
                                                                    color: 'var(--cpm-text-tertiary)',
                                                                }}
                                                            >
                                                                none yet
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ padding: '12px 16px' }}>
                                                <CpmBadge
                                                    tone={
                                                        row.activeStatus === 'Active'
                                                            ? 'success'
                                                            : 'neutral'
                                                    }
                                                    dot
                                                >
                                                    {row.activeStatus}
                                                </CpmBadge>
                                            </div>
                                            <div
                                                className="flex items-center justify-end"
                                                style={{ padding: '12px 16px 12px 0' }}
                                            >
                                                <PencilButton
                                                    label="Open catalogue details"
                                                    onClick={() =>
                                                        setModal({
                                                            mode: 'edit',
                                                            reportID: row.reportID,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <InfiniteScrollSentinel
                                        hasMore={rows.length < total}
                                        loading={moreBusy}
                                        error={moreFailed}
                                        onLoadMore={() => void load(page + 1, true)}
                                        pageSize={50}
                                        showEnd={rows.length > 0}
                                    />
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            ) : (
                <PlaceStateFrames
                    state={place}
                    trueEmpty={trueEmpty}
                    loadingLabel="Waiting for the catalogue. This blank body is not a count of zero reports, and nothing can be created or edited until it arrives."
                    emptyTitle="The catalogue has no reports in it"
                    emptyBody="Nothing exists to enable anywhere yet. This is the catalogue being empty, not a store brand having nothing turned on."
                    emptyAction={
                        <CpmButton
                            variant="navy"
                            size="lg"
                            style={{ height: 42 }}
                            onClick={() => setModal({ mode: 'create' })}
                        >
                            {REPORTS_COPY.NEW_REPORT}
                        </CpmButton>
                    }
                    emptyNote={
                        <>
                            Templates and Store brands are separate.{' '}
                            <Link
                                to="/reports/templates"
                                style={{ color: 'var(--cpm-brand-purple)', fontWeight: 800 }}
                            >
                                Go to Templates
                            </Link>
                            {' · '}
                            <Link
                                to="/reports/brands"
                                style={{ color: 'var(--cpm-brand-purple)', fontWeight: 800 }}
                            >
                                Go to Store brands
                            </Link>
                        </>
                    }
                    errorTitle="Couldn't load the catalogue"
                    errorBody="The list of reports did not come back, so nothing here is a count of zero. Templates and Store brands were not requested by this attempt and have not failed."
                    retryLabel="Retry the catalogue"
                    onRetry={() => void load(1, false)}
                >
                    {null}
                </PlaceStateFrames>
            )}
            {modal ? (
                <ReportFormModal
                    mode={modal.mode}
                    reportID={modal.reportID}
                    onClose={() => setModal(null)}
                    onSaved={(info) => {
                        setSavedNote(info);
                        void load(1, false);
                    }}
                />
            ) : null}
        </div>
    );
}

function MetaStat({ label, tip }: { label: string; tip: { title: string; body: string } }) {
    return (
        <span className="inline-flex items-center" style={{ gap: 6 }}>
            {label}
            <InfoI title={tip.title} body={tip.body} />
        </span>
    );
}
